import { useState, useCallback, useEffect } from "react";
import { InvoiceCountsEntity, GetInvoiceCountsUseCase } from "../../domain";
import { InvoiceRepositoryImpl } from "../../data/repositories/InvoiceRepositoryImpl";

// Create repository instance (singleton)
const invoiceRepository = new InvoiceRepositoryImpl();

// Create use case
const getInvoiceCountsUseCase = new GetInvoiceCountsUseCase(
  invoiceRepository
);

export interface UseInvoiceCountsReturn {
  counts: InvoiceCountsEntity | null;
  loading: boolean;
  error: string | null;
  fetchCounts: (params?: {
    date_from?: string;
    date_to?: string;
  }) => Promise<void>;
  clearError: () => void;
}

export const useInvoiceCounts = (initialParams?: {
  date_from?: string;
  date_to?: string;
}): UseInvoiceCountsReturn => {
  const [counts, setCounts] = useState<InvoiceCountsEntity | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch invoice counts
   */
  const fetchCounts = useCallback(async (params?: {
    date_from?: string;
    date_to?: string;
  }): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const result = await getInvoiceCountsUseCase.execute(params);
      setCounts(result);
    } catch (error: any) {
      const errorMessage = error.message || "Failed to load invoice counts";
      setError(errorMessage);
      console.error("Failed to fetch invoice counts:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Clear error
   */
  const clearError = useCallback((): void => {
    setError(null);
  }, []);

  // Load initial data on mount
  useEffect(() => {
    fetchCounts(initialParams);
  }, [fetchCounts, initialParams?.date_from, initialParams?.date_to]);

  return {
    counts,
    loading,
    error,
    fetchCounts,
    clearError,
  };
};

